import { authFetch, setToken } from './services/api';
import React, { useState, useEffect } from 'react';
import { User, Mail, Calendar, Scale, Heart, X, Check, Lock, Eye, EyeOff } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const UserRegistration = ({ isOpen, onClose, onComplete, activePanel, name360, username360, age360, initialEmail }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoginMode, setIsLoginMode] = useState(activePanel === 'login');

  // Form data - reordered to match new step sequence
  const [formData, setFormData] = useState({
    email: initialEmail || '',
    password: '',
    name: name360 || '',
    username: username360 || '',
    purpose: '',
    age: age360 || '',
    sex: '',
    weight: '',
    weight_unit: 'kg',
    track_menstrual_cycle: false,
    last_period_date: '',
    cycle_length: 28
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation state for real-time feedback
  const [validationStatus, setValidationStatus] = useState({
    email: { state: 'idle', message: '' },
    username: { state: 'idle', message: '' },
    password: { state: 'idle', message: '', strength: '', issues: [] }
  });

  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Login specific state
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const totalSteps = 8; // New total: Email, Password, Name+Username, Goal, Age, Gender, Weight, Cycle

  useEffect(() => {
    setIsLoginMode(activePanel === 'login');
  }, [activePanel]);

  const validateStep = async (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Email
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
          } else {
            // Check email availability via API
            try {
              const response = await authFetch(`${API_BASE_URL}/validate/email/${encodeURIComponent(formData.email)}`);
              const data = await response.json();

              if (!data.available) {
                newErrors.email = data.message;
                setValidationStatus(prev => ({
                  ...prev,
                  email: { state: 'taken', message: data.message }
                }));
              } else {
                setValidationStatus(prev => ({
                  ...prev,
                  email: { state: 'available', message: data.message }
                }));
              }
            } catch (error) {
              console.error('Email validation error:', error);
              // Allow proceeding if validation fails (network error)
            }
          }
        }
        break;

      case 2: // Password
        if (!formData.password || formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters long';
        } else if (validationStatus.password.state === 'invalid') {
          newErrors.password = validationStatus.password.message;
        } else if (validationStatus.password.state !== 'valid') {
          const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&()[\]{}#^+=._-])[A-Za-z\d@$!%*?&()[\]{}#^+=._-]{6,}$/;
          if (!passwordRegex.test(formData.password)) {
            newErrors.password = 'Password must include uppercase, lowercase, number, and special character';
          }
        }
        break;

      case 3: // Name + Username
        if (!formData.name.trim()) {
          newErrors.name = 'Name is required';
        }
        if (!formData.username.trim()) {
          newErrors.username = 'Username is required';
        } else if (validationStatus.username.state === 'checking') {
          newErrors.username = 'Checking username availability...';
        } else if (validationStatus.username.state === 'taken' || validationStatus.username.state === 'invalid') {
          newErrors.username = validationStatus.username.message;
        }
        break;

      case 4: // Goal
        if (!formData.purpose) {
          newErrors.purpose = 'Please select your goal';
        }
        break;

      case 5: // Age
        if (!formData.age || formData.age < 1 || formData.age > 120) {
          newErrors.age = 'Please enter a valid age (1-120)';
        }
        break;

      case 6: // Gender
        if (!formData.sex) {
          newErrors.sex = 'Please select your gender';
        }
        break;

      case 7: // Weight
        const unit = formData.weight_unit || 'kg';
        const w = parseFloat(formData.weight);
        const ok = !isNaN(w) && (
          (unit === 'kg' && w >= 20 && w <= 500) ||
          (unit === 'lb' && w >= 44 && w <= 1100)
        );
        if (!ok) {
          newErrors.weight = unit === 'kg'
            ? 'Please enter a valid weight (20–500 kg)'
            : 'Please enter a valid weight (44–1100 lb)';
        }
        break;

      case 8: // Menstrual Tracking (only for females)
        if (formData.sex === 'Female' && formData.track_menstrual_cycle) {
          if (!formData.last_period_date) {
            newErrors.last_period_date = 'Please enter the start date of your last period';
          } else {
            const periodDate = new Date(formData.last_period_date);
            const today = new Date();
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(today.getMonth() - 3);

            if (periodDate > today) {
              newErrors.last_period_date = 'Period date cannot be in the future';
            } else if (periodDate < threeMonthsAgo) {
              newErrors.last_period_date = 'Please enter a date within the last 3 months for accurate tracking';
            }
          }

          const cycleLen = parseInt(formData.cycle_length);
          if (isNaN(cycleLen) || cycleLen < 21 || cycleLen > 45) {
            newErrors.cycle_length = 'Cycle length must be between 21 and 45 days';
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      // Skip menstrual tracking step for males
      if (currentStep === 8 && formData.sex === 'Male') {
        handleSubmit();
      } else if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleLogin = async () => {
    if (!loginPassword) {
      setLoginError('Please enter your password');
      return;
    }

    setIsSubmitting(true);
    setLoginError('');

    try {
      console.log('Attempting login to:', `${API_BASE_URL}/login`);

      const response = await authFetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: initialEmail,
          password: loginPassword
        })
      });

      console.log('Login response status:', response.status);

      if (response.ok) {
        const text = await response.text();

        let data;
        try {
          data = JSON.parse(text);
        } catch {
          console.error("Non-JSON response:", text);
          throw new Error("Backend returned non-JSON response");
        }
        console.log('Login successful:', data);
        setToken(data.token);
        localStorage.setItem('mealPlannerUser', JSON.stringify(data.user));
        onComplete({ user: data.user });
        onClose();
      } else {
        const errorData = await response.json();
        setLoginError(errorData.error || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Login failed. Please check if backend is running on port 3001.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    console.log('Submitting user data:', formData);

    try {
      const registrationData = {
        name: formData.name.trim(),
        username: formData.username.trim(),
        age: parseInt(formData.age),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        sex: formData.sex,
        weight: parseFloat(formData.weight),
        weight_unit: formData.weight_unit,
        purpose: formData.purpose.trim(),
        track_menstrual_cycle: formData.sex === 'Female' ? formData.track_menstrual_cycle : false,
        last_period_date: formData.sex === 'Female' && formData.track_menstrual_cycle ? formData.last_period_date : null,
        cycle_length: formData.sex === 'Female' && formData.track_menstrual_cycle ? parseInt(formData.cycle_length) : 28
      };

      console.log('Sending registration to:', `${API_BASE_URL}/users`);
      console.log('Registration data:', registrationData);

      const response = await authFetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const userData = await response.json();
        console.log('User registered successfully:', userData);
        setToken(userData.token);
        localStorage.setItem('mealPlannerUser', JSON.stringify(userData.user));
        alert('Registration successful! Welcome to Meal Planner!');
        onComplete(userData);
        onClose();
      } else {
        const errorData = await response.json();
        console.error('Registration failed:', errorData);

        // Display user-friendly error message
        if (errorData.error) {
          setErrors({ submit: errorData.error });
        } else {
          throw new Error(`HTTP ${response.status}: Registration failed`);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        submit: `Registration failed: ${error.message}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen && username360 && !formData.username) {
      setFormData(prev => ({
        ...prev,
        email: initialEmail,
        username: username360,
        name: name360,
        age: age360
      }));
    }
  }, [username360, name360, age360, isOpen, initialEmail]);

  // Debounce utility function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Real-time email validation with debounce
  const validateEmailAvailability = React.useCallback(
    debounce(async (email) => {
      if (!email || !email.trim()) {
        setValidationStatus(prev => ({ ...prev, email: { state: 'idle', message: '' } }));
        return;
      }

      setValidationStatus(prev => ({ ...prev, email: { state: 'checking', message: 'Checking availability...' } }));

      try {
        const response = await authFetch(`${API_BASE_URL}/validate/email/${encodeURIComponent(email)}`);
        const data = await response.json();

        setValidationStatus(prev => ({
          ...prev,
          email: {
            state: data.available ? 'available' : 'taken',
            message: data.message
          }
        }));
      } catch (error) {
        console.error('Email validation error:', error);
        setValidationStatus(prev => ({
          ...prev,
          email: { state: 'error', message: 'Could not validate email' }
        }));
      }
    }, 500),
    []
  );

  // Real-time username validation with debounce
  const validateUsernameAvailability = React.useCallback(
    debounce(async (username) => {
      if (!username || !username.trim()) {
        setValidationStatus(prev => ({ ...prev, username: { state: 'idle', message: '' } }));
        return;
      }

      setValidationStatus(prev => ({ ...prev, username: { state: 'checking', message: 'Checking availability...' } }));

      try {
        const response = await authFetch(`${API_BASE_URL}/validate/username/${encodeURIComponent(username)}`);
        const data = await response.json();

        setValidationStatus(prev => ({
          ...prev,
          username: {
            state: data.available ? 'available' : (data.valid ? 'taken' : 'invalid'),
            message: data.message
          }
        }));
      } catch (error) {
        console.error('Username validation error:', error);
        setValidationStatus(prev => ({
          ...prev,
          username: { state: 'error', message: 'Could not validate username' }
        }));
      }
    }, 500),
    []
  );

  // Real-time password validation with debounce
  const validatePasswordStrength = React.useCallback(
    debounce(async (password) => {
      if (!password || !password.trim()) {
        setValidationStatus(prev => ({
          ...prev,
          password: { state: 'idle', message: '', strength: '', issues: [] }
        }));
        return;
      }

      try {
        const response = await authFetch(`${API_BASE_URL}/validate/password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        });
        const data = await response.json();

        setValidationStatus(prev => ({
          ...prev,
          password: {
            state: data.valid ? 'valid' : 'invalid',
            message: data.message,
            strength: data.strength,
            issues: data.issues || []
          }
        }));
      } catch (error) {
        console.error('Password validation error:', error);
        setValidationStatus(prev => ({
          ...prev,
          password: { state: 'error', message: 'Could not validate password', strength: '', issues: [] }
        }));
      }
    }, 300),
    []
  );

  // Auto-suggest username from name
  const generateUsername = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .substring(0, 30);
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Trigger validation for specific fields (NOT email - only on Next click)
    if (field === 'username') {
      validateUsernameAvailability(value);
    } else if (field === 'password') {
      validatePasswordStrength(value);
    }
  };

  const renderLoginPanel = () => {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <User className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back!</h3>
          <p className="text-gray-600">Login to continue</p>
        </div>

        {loginError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
            {loginError}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={initialEmail}
            disabled
            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            type="password"
            placeholder="Enter your password"
            value={loginPassword}
            onChange={(e) => {
              setLoginPassword(e.target.value);
              setLoginError('');
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleLogin();
              }
            }}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
            autoFocus
          />
        </div>

        <button
          onClick={handleLogin}
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 font-semibold"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Logging in...</span>
            </div>
          ) : (
            'Login'
          )}
        </button>

        <div className="text-center">
          <button
            onClick={() => {
              setLoginPassword('');
              setLoginError('');
              setIsLoginMode(false);
              setCurrentStep(1);
            }}
            className="text-orange-600 hover:text-orange-700 text-sm font-medium"
          >
            Don't have an account? Sign up
          </button>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: // Email (First step)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">📧</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">What's your email?</h3>
              <p className="text-gray-600">We'll use this to save your meal plans and preferences</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.email ? 'border-red-500' : validationStatus.email.state === 'available' ? 'border-green-500' : 'border-gray-200'
                    }`}
                  autoFocus
                />
                {validationStatus.email.state === 'checking' && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {validationStatus.email.state === 'available' && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {validationStatus.email.state === 'taken' && (
                  <X className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {validationStatus.email.state === 'available' && (
                <p className="text-green-600 text-sm mt-2 flex items-center">
                  <Check className="w-4 h-4 mr-1" /> {validationStatus.email.message}
                </p>
              )}
              {validationStatus.email.state === 'taken' && (
                <div className="mt-3 text-center">
                  <p className="text-red-600 text-sm flex items-center justify-center mb-2">
                    <X className="w-4 h-4 mr-1" /> {validationStatus.email.message}
                  </p>
                  <button
                    onClick={() => {
                      setIsLoginMode(true);
                      setCurrentStep(1);
                    }}
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium inline-flex items-center gap-1"
                  >
                    🔑 Login instead
                  </button>
                </div>
              )}
              {errors.email && validationStatus.email.state !== 'taken' && (
                <p className="text-red-500 text-sm mt-2">{errors.email}</p>
              )}

            </div>
          </div>
        );

      case 2: // Password (Second step)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">🔒</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Create a secure password</h3>
              <p className="text-gray-600">Keep your account safe with a strong password</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  onFocus={() => setShowPasswordRequirements(true)}
                  className={`w-full px-4 py-3 pr-12 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.password ? 'border-red-500' : validationStatus.password.state === 'valid' ? 'border-green-500' : 'border-gray-200'
                    }`}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {(formData.password || showPasswordRequirements) && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {['At least 6 characters', 'One uppercase letter', 'One lowercase letter', 'One number', 'One special character'].map((req, index) => {
                      const isMet = validationStatus.password.issues && !validationStatus.password.issues.includes(req);
                      const isReallyMet = formData.password && isMet;

                      return (
                        <div key={index} className="flex items-center text-xs">
                          {isReallyMet ? (
                            <Check className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" />
                          ) : (
                            <span className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0">○</span>
                          )}
                          <span className={isReallyMet ? 'text-green-600' : 'text-gray-500'}>{req}</span>
                        </div>
                      );
                    })}
                  </div>
                  {formData.password && validationStatus.password.strength && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-gray-600">Strength:</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full transition-all ${validationStatus.password.strength === 'strong' ? 'bg-green-500 w-full' :
                          validationStatus.password.strength === 'medium' ? 'bg-yellow-500 w-2/3' :
                            'bg-red-500 w-1/3'
                          }`} />
                      </div>
                      <span className={`text-xs font-medium ${validationStatus.password.strength === 'strong' ? 'text-green-600' :
                        validationStatus.password.strength === 'medium' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                        {validationStatus.password.strength.charAt(0).toUpperCase() + validationStatus.password.strength.slice(1)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {errors.password && !showPasswordRequirements && <p className="text-red-500 text-sm mt-2">{errors.password}</p>}
            </div>
          </div>
        );

      case 3: // Name + Username (Third step)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">👤</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Tell us about yourself</h3>
              <p className="text-gray-600">Your name and username for your profile</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.name ? 'border-red-500' : 'border-gray-200'
                  }`}
                autoFocus
              />
              {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="john_doe"
                  value={formData.username}
                  onChange={(e) => updateFormData('username', e.target.value)}
                  className={`w-full px-4 py-3 pr-12 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.username ? 'border-red-500' : validationStatus.username.state === 'available' ? 'border-green-500' : 'border-gray-200'
                    }`}
                />
                {validationStatus.username.state === 'checking' && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {validationStatus.username.state === 'available' && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {(validationStatus.username.state === 'taken' || validationStatus.username.state === 'invalid') && (
                  <X className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {validationStatus.username.state === 'available' && (
                <p className="text-green-600 text-sm mt-2 flex items-center">
                  <Check className="w-4 h-4 mr-1" /> {validationStatus.username.message}
                </p>
              )}
              {(validationStatus.username.state === 'taken' || validationStatus.username.state === 'invalid') && (
                <p className="text-red-600 text-sm mt-2 flex items-center">
                  <X className="w-4 h-4 mr-1" /> {validationStatus.username.message}
                </p>
              )}
              {errors.username && <p className="text-red-500 text-sm mt-2">{errors.username}</p>}
              <p className="text-xs text-gray-500 mt-2">3-255 characters, letters, numbers, and underscores only</p>
            </div>
          </div>
        );

      case 4: // Goal
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">🎯</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">What brings you here?</h3>
              <p className="text-gray-600">Tell us your health and nutrition goals</p>
            </div>
            <div className="space-y-3">
              {[
                'Weight Loss',
                'Weight Gain',
                'Maintain Weight',
                'Build Muscle',
                'Improve Health',
                'Save Time',
                'Learn to Cook',
                'Family Meal Planning'
              ].map((goal) => (
                <button
                  key={goal}
                  onClick={() => updateFormData('purpose', goal)}
                  className={`w-full p-4 border-2 rounded-xl text-left transition-all hover:border-orange-500 ${formData.purpose === goal
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200'
                    }`}
                >
                  <span className="font-semibold text-lg">{goal}</span>
                </button>
              ))}
            </div>
            {errors.purpose && <p className="text-red-500 text-sm mt-2">{errors.purpose}</p>}
          </div>
        );

      case 5: // Age
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">🎂</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">What's your age?</h3>
              <p className="text-gray-600">Helps us personalize your nutrition recommendations</p>
            </div>
            <div>
              <input
                type="number"
                placeholder="25"
                value={formData.age}
                onChange={(e) => updateFormData('age', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.age ? 'border-red-500' : 'border-gray-200'}`}
                autoFocus
                min="1"
                max="120"
              />
              {errors.age && <p className="text-red-500 text-sm mt-2">{errors.age}</p>}
            </div>
          </div>
        );

      case 6: // Gender
        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-5xl mb-4 inline-block">⚧️</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">What's your gender?</h3>
              <p className="text-gray-600">This helps us calculate your nutritional needs</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {['Male', 'Female'].map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('sex', option)}
                  className={`p-4 border-2 rounded-xl transition-all ${formData.sex === option
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-orange-300'
                    }`}
                >
                  <span className="text-lg font-medium">{option}</span>
                </button>
              ))}
            </div>
            {errors.sex && <p className="text-red-500 text-sm mt-2">{errors.sex}</p>}
          </div>
        );

      case 7: // Weight
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Scale className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">What's your weight?</h3>
              <p className="text-gray-600">This helps us calculate your daily calorie needs</p>
            </div>

            <div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Enter your weight"
                    value={formData.weight}
                    onChange={(e) => updateFormData('weight', e.target.value)}
                    className={`w-full px-4 py-3 pr-12 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.weight ? 'border-red-500' : 'border-gray-200'
                      }`}
                    min={formData.weight_unit === 'lb' ? 44 : 20}
                    max={formData.weight_unit === 'lb' ? 1100 : 500}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                    {formData.weight_unit === 'lb' ? 'lb' : 'kg'}
                  </span>
                </div>

                <select
                  value={formData.weight_unit}
                  onChange={(e) => updateFormData('weight_unit', e.target.value)}
                  className="px-3 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="kg">kg</option>
                  <option value="lb">lb</option>
                </select>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Enter the number in the selected unit.
              </p>

              {errors.weight && <p className="text-red-500 text-sm mt-2">{errors.weight}</p>}
            </div>
          </div>
        );

      case 8: // Menstrual Cycle (Eighth Step)
        // If Male, show completion summary or auto-finish (handled in handleNext, but for render safety:)
        if (formData.sex !== 'Female') {
          return (
            <div className="space-y-6 text-center animate-fade-in">
              <span className="text-6xl mb-4 inline-block">🎉</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">You're all set!</h3>
              <p className="text-gray-600">We have everything we need to create your personalized meal plan.</p>
              <div className="p-6 bg-green-50 rounded-xl border border-green-100 mt-6">
                <p className="text-green-800 font-medium">Ready to start your journey?</p>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div className="text-center">
              <span className="text-6xl mb-4 inline-block">🌸</span>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Menstrual Cycle Tracking</h3>
              <p className="text-gray-600">Get personalized meal recommendations based on your cycle phase</p>
            </div>

            <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-xl border border-pink-200">
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  id="track_cycle"
                  checked={formData.track_menstrual_cycle}
                  onChange={(e) => updateFormData('track_menstrual_cycle', e.target.checked)}
                  className="mt-1 w-5 h-5 text-pink-500 rounded focus:ring-2 focus:ring-pink-500"
                />
                <label htmlFor="track_cycle" className="flex-1 cursor-pointer">
                  <div className="font-medium text-gray-800">
                    Yes, I want personalized nutrition recommendations during my cycle
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    We'll suggest meals rich in iron, magnesium, and other nutrients your body needs during different cycle phases. Your data is private and secure. 🔒
                  </div>
                </label>
              </div>
            </div>

            {formData.track_menstrual_cycle && (
              <div className="space-y-4 animate-fade-in">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    When did your last period start?
                  </label>
                  <input
                    type="date"
                    value={formData.last_period_date}
                    onChange={(e) => updateFormData('last_period_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-pink-500 ${errors.last_period_date ? 'border-red-500' : 'border-gray-200'
                      }`}
                  />
                  {errors.last_period_date && (
                    <p className="text-red-500 text-sm mt-2">{errors.last_period_date}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    This helps us predict your next period and adjust recommendations accordingly
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What's your typical cycle length? (Days between periods)
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="21"
                      max="45"
                      value={formData.cycle_length}
                      onChange={(e) => updateFormData('cycle_length', e.target.value)}
                      className="flex-1 h-2 bg-pink-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${((formData.cycle_length - 21) / 24) * 100}%, #fce7f3 ${((formData.cycle_length - 21) / 24) * 100}%, #fce7f3 100%)`
                      }}
                    />
                    <span className="text-2xl font-bold text-pink-600 min-w-[60px] text-center">
                      {formData.cycle_length}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>21 days</span>
                    <span className="font-medium text-pink-600">Average: 28 days</span>
                    <span>45 days</span>
                  </div>
                  {errors.cycle_length && (
                    <p className="text-red-500 text-sm mt-2">{errors.cycle_length}</p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">💡</span>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">How this helps:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Suggests iron-rich foods during your period</li>
                        <li>Recommends anti-bloating meals before your period</li>
                        <li>Offers healthy alternatives for cravings</li>
                        <li>Adjusts nutrition based on cycle phase</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!formData.track_menstrual_cycle && (
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  You can always enable cycle tracking later in your dashboard settings
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100 flex-shrink-0">
          {isLoginMode ? (
            <span className="text-lg font-semibold text-gray-800">Login</span>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {currentStep}
              </div>
              <span className="text-lg font-semibold text-gray-800">
                Step {currentStep} of {totalSteps}
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar - Only show in signup mode */}
        {!isLoginMode && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-500 to-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {isLoginMode ? renderLoginPanel() : renderStep()}

          {errors.submit && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* Footer - Only show in signup mode */}
        {!isLoginMode && (
          <div className="flex justify-between items-center p-6 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-full font-medium transition-all ${currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-orange-600 hover:bg-orange-50'
                }`}
            >
              Back
            </button>

            <div className="text-xs text-gray-500 hidden md:block">
              Step {currentStep}/{totalSteps}
            </div>

            <button
              onClick={handleNext}
              disabled={isSubmitting || (currentStep === totalSteps && !formData.purpose)}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-full font-semibold hover:from-orange-600 hover:to-green-600 transition-all disabled:opacity-50 flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  {currentStep === totalSteps ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Complete</span>
                    </>
                  ) : (
                    <span>Next</span>
                  )}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserRegistration;